import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/seo/SEO';
import SchemaMarkup from '../../components/seo/SchemaMarkup';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useLanguageLink } from '../../hooks/useLanguageLink';

export default function ContactPage() {
  const { t } = useTranslation('contact');
  const { user } = useAuthStore();
  const { createLink } = useLanguageLink();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Call contact API - only send name/email if not authenticated
      const payload: any = {
        subject: formData.subject,
        message: formData.message,
      };

      // For non-authenticated users, include name and email
      if (!user) {
        payload.name = formData.name;
        payload.email = formData.email;
      }

      const response = await api.post('/api/contact', payload);

      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });

      // Reset success message after 5 seconds
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (error: any) {
      console.error('Contact form submission error:', error);
      setIsSubmitting(false);
      setSubmitStatus('error');

      // Reset error message after 5 seconds
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const contactMethods = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: t('methods.email.title'),
      description: t('methods.email.description'),
      contact: 'support@axetra.io',
      link: 'mailto:support@axetra.io',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      title: t('methods.support.title'),
      description: t('methods.support.description'),
      contact: 'support@axetra.io',
      link: 'mailto:support@axetra.io',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: t('methods.sales.title'),
      description: t('methods.sales.description'),
      contact: 'sales@axetra.io',
      link: 'mailto:sales@axetra.io',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags */}
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        canonical="/contact"
        keywords="contact axetra, traffic filtering support, bot detection help, customer support"
      />

      {/* Schema Markup */}
      <SchemaMarkup type="organization" />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 via-white to-purple-50">
        <div className="container-marketing">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-600 to-purple-600">
                {t('hero.title')}
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {t('hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="container-marketing">
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {contactMethods.map((method, index) => (
              <a
                key={index}
                href={method.link}
                className="text-center p-6 rounded-xl border-2 border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all duration-200 group"
              >
                <div className="w-12 h-12 bg-gradient-to-r from-primary-600 to-purple-600 rounded-lg flex items-center justify-center mx-auto mb-4 text-white group-hover:scale-110 transition-transform">
                  {method.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{method.title}</h3>
                <p className="text-sm text-gray-600 mb-2">{method.description}</p>
                <p className="text-primary-600 font-semibold">{method.contact}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="container-marketing max-w-4xl">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('form.title')}</h2>
              <p className="text-gray-600">
                {user ? t('form.subtitle_logged_in') : t('form.subtitle_logged_out')}
              </p>
            </div>

            {!user && (
              <div className="mb-6 p-6 bg-blue-50 border-2 border-blue-200 rounded-lg text-center">
                <div className="flex flex-col items-center gap-4">
                  <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <div>
                    <p className="text-lg font-semibold text-blue-900 mb-2">{t('form.login_required.title')}</p>
                    <p className="text-blue-700 mb-4">{t('form.login_required.description')}</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Link
                        to={createLink('login')}
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-all duration-200"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                        </svg>
                        {t('form.login_required.login_button')}
                      </Link>
                      <a
                        href="mailto:support@axetra.io"
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-primary-600 border-2 border-primary-200 rounded-lg font-semibold hover:border-primary-400 hover:bg-primary-50 transition-all duration-200"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {t('form.login_required.email_button')}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {user && submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="font-semibold text-green-900">{t('form.success_title')}</p>
                    <p className="text-sm text-green-700">{t('form.success_message')}</p>
                  </div>
                </div>
              </div>
            )}

            {user && submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="font-semibold text-red-900">{t('form.error_title')}</p>
                    <p className="text-sm text-red-700">{t('form.error_message')}</p>
                  </div>
                </div>
              </div>
            )}

            {user && <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="subject" className="label">
                  {t('form.subject_label')} *
                </label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="input"
                >
                  <option value="">{t('form.subject_placeholder')}</option>
                  <option value="general">{t('form.subject_options.general')}</option>
                  <option value="support">{t('form.subject_options.support')}</option>
                  <option value="sales">{t('form.subject_options.sales')}</option>
                  <option value="partnership">{t('form.subject_options.partnership')}</option>
                  <option value="feedback">{t('form.subject_options.feedback')}</option>
                  <option value="other">{t('form.subject_options.other')}</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="label">
                  {t('form.message_label')} *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="input resize-none"
                  placeholder={t('form.message_placeholder')}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary w-full md:w-auto px-8 py-3 text-base disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('form.submitting')}
                  </>
                ) : (
                  <>
                    {t('form.submit_button')}
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </>
                )}
              </button>
            </form>}
          </div>
        </div>
      </section>

      {/* FAQ Link Section */}
      <section className="py-16 bg-white">
        <div className="container-marketing text-center max-w-3xl">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('faq_section.title')}</h3>
          <p className="text-gray-600 mb-6">
            {t('faq_section.description')}
          </p>
          <Link
            to={createLink('faq')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary-600 border-2 border-primary-200 rounded-lg font-semibold hover:border-primary-400 hover:bg-primary-50 transition-all duration-200"
          >
            {t('faq_section.button')}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </section>

    </div>
  );
}
